import { Component, EventEmitter, OnInit, Output, ViewChild, ChangeDetectorRef } from '@angular/core';
import { FormGroup, Validators, FormControl } from '@angular/forms';
import { ContaBancariaService } from '../../../services/conta-bancaria.service';
import { ContaBancaria } from '../../../models/contaBancaria.model';
import { ToastService } from '../../../services/toast.service';
import { SeveridadeToast } from '../../../models/enums/severidade-toast.enum';
import { forkJoin, of, Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { FileUpload } from 'primeng/fileupload';
import { ExtratoService } from '../../../services/importacao-extrato.service';
import { Extrato } from '../../../models/extrato.model';
import { TipoTransacao } from '../../../models/enums/tipoTransacao.enum';
import { LinhaExtrato } from '../../../models/linhaExtrato.model';
import { Router, NavigationStart } from '@angular/router';
import { SpinnerService } from '../../../services/spinner.service';
import { PlanoDeContasService } from '../../../services/plano-de-contas.service';
import { FormataDataHelper } from '../../../helpers/formata-data.helper';
import { VerificacaoExistenciaExtratoRequest } from '../../../models/verificacaoExistenciaExtratoRequest.model'
import { ConfirmationService, MessageService } from 'primeng/api';
import { Icones } from '../../../shared/variaveisDeRotas';
import { JwtHelper } from '../../../helpers/jwtDecode.helper';
import { AuthService } from '../../../services/auth.service';
import { UsuarioAllProperties } from '../../../models/usuarioAllProperties.model';
import { retornaDescricao } from '../../../shared/mock/descricao';
import { InfoDialogComponent, InfoDialogConfig } from '../../../shared/info-dialog/info-dialog.component';

@Component({
  selector: 'app-importacao-extrato-bancario',
  templateUrl: './importacao-extrato-bancario.component.html',
  styleUrls: ['./importacao-extrato-bancario.component.scss'],
  providers: [ConfirmationService]
})
export class ImportarExtratosBancarios implements OnInit {
  @ViewChild('fileUploadOfx') fileUploadOfx!: FileUpload;
  @ViewChild('fileUploadXlsx') fileUploadXlsx!: FileUpload;
  @Output() tituloChange = new EventEmitter<string>();
  @Output() iconeChange = new EventEmitter<string>();

  @Output() tituloDescricao = new EventEmitter<string>();
  @Output() textoDescricao = new EventEmitter<string>();
  @Output() mostrarDescricao = new EventEmitter<boolean>();

  importForm: FormGroup = new FormGroup({
    contaBancariaId: new FormControl('', Validators.required),
    arquivosExtratos: new FormControl([]),
    data: new FormControl('', Validators.required)
  });

  steps = [
    { label: 'Selecionar Conta' },
    { label: 'Importar Arquivo' },
    { label: 'Confirmar Importação' },
  ];

  activeIndex = 0;
  contasBancarias: any[] = [];
  contasContabeis: any[] = [];
  extratos: Extrato[] = [];
  uploadedFiles: any[] = [];
  usuario: UsuarioAllProperties;
  dataCriacaoUsuario: Date;
  private bancoCache: { [codigo: string]: string } = {};
  private currentNavigationUrl: string = '';
  private skipCanDeactivate = false;
  private userId: string;
  displayDialog: boolean = false;
  displayDialogSemMovimentacao: boolean = false;
  importarConfirmada: boolean = false;
  infoDialogConfig: InfoDialogConfig = {
    title: 'Importação de Extrato Bancário',
    messageTitle: 'Nenhuma movimentação encontrada',
    message: 'O extrato bancário está sem movimentação para o período selecionado. Não há necessidade de importação.',
    icon: 'pi pi-info-circle',
    iconColor: '#2196F3',
    buttonLabel: 'OK',
    buttonIcon: 'pi pi-check',
    closable: false
  };
  selectedFileExtension: string = '';
  hasSelectedFile: boolean = false;

  constructor(
    private toastService: ToastService,
    private contaBancariaService: ContaBancariaService,
    private extratoService: ExtratoService,
    private confirmationService: ConfirmationService,
    private router: Router,
    private spinnerService: SpinnerService,
    private planoDeContasService: PlanoDeContasService,
    private formataDataHelper: FormataDataHelper,
    private jwtHelper: JwtHelper,
    private authService: AuthService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef
  ) {
    this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationStart) {
        localStorage.setItem('importForm', JSON.stringify(this.importForm.value));
      }
    });
    this.userId = this.jwtHelper.getUserIdFromToken();
  }

  ngOnInit(): void {
    this.tituloChange.emit('Importação de Extrato Bancário');
    this.iconeChange.emit(Icones.arquivoAmarelo);
    this.tituloDescricao.emit(retornaDescricao('importarExtratoBancario').titulo);
    this.textoDescricao.emit(retornaDescricao('importarExtratoBancario').texto);
    this.mostrarDescricao.emit(true)

    this.limparCache();

    this.retornaTodasPropriedadesUsuario(this.userId);

    const savedForm = localStorage.getItem('importForm');
    if (savedForm) {
      this.importForm.setValue(JSON.parse(savedForm));
    } else {
      this.activeIndex = 0;
    }

    this.carregarDadosIniciais();

    this.extratoService.getFiles().subscribe((files) => {
      this.uploadedFiles = files;
    });

    this.extratoService.getExtratos().subscribe((extratos) => {
      if (extratos.length > 0) {
        this.extratos = extratos.map(extrato => ({
          ...extrato,
          linhasExtratos: extrato.linhasExtratos?.map(linha => ({
            ...linha,
            permiteAlterarContaContrapartida: linha.permiteAlterarContaContrapartida ?? false
          })) || []
        }));

        if (this.importForm.get('contaBancariaId')?.value && this.importForm.get('data')?.value && this.uploadedFiles.length > 0) {
          this.activeIndex = 2;
        } else {
          this.activeIndex = 0;
        }
      } else {
        this.activeIndex = 0;
      }
    });
  }

  ngOnDestroy(): void {
    this.mostrarDescricao.emit(false);
    this.limparCache();
  }

  baixarPlanilhaModelo(): void {
    this.extratoService.baixarPlanilhaModelo().subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'Planilha Modelo_Extrato Bancario.xlsx';
        anchor.click();
        window.URL.revokeObjectURL(url);
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Download realizado com sucesso.',
        });
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Falha ao realizar o download da planilha.',
        });
        console.error(err);
      },
    });
  }

  limpaUploadedFiles(): void {
    this.uploadedFiles = [];
    this.fileUploadOfx.clear();
    this.fileUploadXlsx.clear();
    this.extratoService.setFiles(this.uploadedFiles);
    const ctrl = this.importForm.get('arquivosExtratos');
    ctrl?.setValue([]);
    ctrl?.markAsDirty();
    ctrl?.updateValueAndValidity();
    this.hasSelectedFile = false;
    this.cdr.detectChanges();
  }

  retornaTodasPropriedadesUsuario(userId: string) {
    this.authService.obterUsuarioAllPropertiesPorId(userId).subscribe({
      next: (res) => {
        this.usuario = res;
        this.retornaDataCriacaoUsuario(this.usuario)
      },
      error: (e) => {
        this.toastService.setMensagemToast(`Erro ao obter usuário.`, SeveridadeToast.Error);
      }
    })
  }

  retornaDataCriacaoUsuario(usuario: UsuarioAllProperties) {
    const raw = usuario.createdAt;
    if (!raw) {
      this.toastService.setMensagemToast(
        'Não foi possível obter a data de criação da conta. Atualize a página ou entre em contato com o suporte.',
        SeveridadeToast.Error
      );
      this.dataCriacaoUsuario = new Date();
      return;
    }
    const fullDate = new Date(raw);
    this.dataCriacaoUsuario = new Date(fullDate.getFullYear(), fullDate.getMonth(), fullDate.getDate());
  }

  canDeactivate(): Observable<boolean> | boolean {
    if (this.skipCanDeactivate) {
      return true;
    }
    if (this.activeIndex === 2 && !this.currentNavigationUrl.includes('/detalhes-extrato')) {
      const confirmLeave = window.confirm(
        'Você tem certeza de que deseja sair desta página? A página será atualizada ao voltar.'
      );
      if (confirmLeave) {
        this.extratoService.resetState();
      }
      return confirmLeave;
    }
    return true;
  }



  onFileSelectOfx(event: any) {
    this.uploadedFiles = [...event.files];
    const files = Array.isArray(event.files) ? event.files : Array.from(event.files);
    if (files.length > 0) {
      const ofxFiles = files.filter((file: File) =>
        file.name.toLowerCase().endsWith('.ofx')
      );
      if (ofxFiles.length !== files.length) {
        this.toastService.setMensagemToast(
          'Apenas arquivos .ofx são permitidos',
          SeveridadeToast.Error
        );
        this.uploadedFiles = [];
      } else {
        this.uploadedFiles = ofxFiles;
        const ctrl = this.importForm.get('arquivosExtratos');
        ctrl?.setValue(this.uploadedFiles);
        ctrl?.markAsDirty();
        ctrl?.updateValueAndValidity();
        this.extratoService.setFiles(this.uploadedFiles);
        this.hasSelectedFile = this.uploadedFiles.length > 0;
        this.cdr.detectChanges();
        setTimeout(() => {
          this.hasSelectedFile = this.uploadedFiles.length > 0;
          this.cdr.detectChanges();
        });
      }
    }
  }

  onFileSelectXlsx(event: any) {
    this.uploadedFiles = [...event.files];
    const files = Array.isArray(event.files) ? event.files : Array.from(event.files);
    if (files.length > 0) {
      const ofxFiles = files.filter((file: File) =>
        file.name.toLowerCase().endsWith('.xlsx')
      );
      if (ofxFiles.length !== files.length) {
        this.toastService.setMensagemToast(
          'Apenas arquivos .xlsx são permitidos',
          SeveridadeToast.Error
        );
        this.uploadedFiles = [];
      } else {
        this.uploadedFiles = ofxFiles;
        const ctrl = this.importForm.get('arquivosExtratos');
        ctrl?.setValue(this.uploadedFiles);
        ctrl?.markAsDirty();
        ctrl?.updateValueAndValidity();
        this.extratoService.setFiles(this.uploadedFiles);
        this.hasSelectedFile = this.uploadedFiles.length > 0;
        this.cdr.detectChanges();
        setTimeout(() => {
          this.hasSelectedFile = this.uploadedFiles.length > 0;
          this.cdr.detectChanges();
        });
      }
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024,
      dm = 2,
      sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'],
      i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  onNextStep() {
  if (this.activeIndex === 0) {
    const contaValida = this.importForm.get('contaBancariaId')?.valid;
    const dataValida = this.importForm.get('data')?.valid;

    if (!contaValida || !dataValida) {
      this.toastService.setMensagemToast(
        'Por favor, selecione o banco e a data para continuar.',
        SeveridadeToast.Error
      );
      return;
    }

    this.activeIndex++;
    return;
  }
  if (this.activeIndex === 1) {
    const arquivosSelecionados = this.getArquivosSelecionados();
    if (arquivosSelecionados.length === 0) {
      this.toastService.setMensagemToast(
        'Por favor, selecione um arquivo para upload.',
        SeveridadeToast.Error
      );
      return;
    }

    this.importForm.get('arquivosExtratos')?.setValue(arquivosSelecionados);
    this.uploadedFiles = arquivosSelecionados;

    const fileName = arquivosSelecionados[0]?.name;
    const fileExtension = fileName ? fileName.split('.').pop()?.toLowerCase() : '';

    this.selectedFileExtension = fileExtension || '';

    const contaBancariaId = this.importForm.get('contaBancariaId')?.value;

    if (fileExtension === 'ofx') {
      let formData = this.montarFormData();
      this.retornarLinhasExtratoOfx(formData);
    } else if (fileExtension === 'xlsx') {
      let formData = this.montarFormData();
      this.retornarLinhasExtratoPlanilha(formData);
    } else {
      this.toastService.setMensagemToast(
        'Por favor, selecione um arquivo com extensão válida (.ofx ou .xlsx).',
        SeveridadeToast.Error
      );
      return;
    }
    return;
  }
  if (this.activeIndex < this.steps.length - 1) {
    this.activeIndex++;
  }
}

  private getArquivosSelecionados(): File[] {
    const fromControl: File[] = this.importForm.get('arquivosExtratos')?.value || [];
    const fromState: File[] = this.uploadedFiles || [];
    const ofxComp: any[] = (this.fileUploadOfx && (this.fileUploadOfx as any).files) ? (this.fileUploadOfx as any).files : [];
    const xlsxComp: any[] = (this.fileUploadXlsx && (this.fileUploadXlsx as any).files) ? (this.fileUploadXlsx as any).files : [];
    const combined = [...fromControl, ...fromState, ...ofxComp, ...xlsxComp];
    return combined.filter((f, idx) => !!f && combined.indexOf(f) === idx);
  }

  onPreviousStep() {
    if (this.activeIndex > 0) {
      this.activeIndex--;
      if (this.fileUploadOfx || this.fileUploadXlsx) {
        this.fileUploadOfx.clear();
        this.fileUploadXlsx.clear();
      } else {
        console.error('fileUpload não está definido.');
      }
    }
  }

  montarFormData(): FormData {
    const formData = new FormData();

    const contaBancariaId = this.importForm.get('contaBancariaId')?.value.toString();
    const idToSend = contaBancariaId === '' ? this.extratos[0].contaBancaria.id.toString() : contaBancariaId;
    formData.append('ContaBancariaId', idToSend);

    const arquivosExtratos = this.importForm.get('arquivosExtratos')?.value || [];
    arquivosExtratos.forEach((file: File) => {
      formData.append('ArquivosExtratos', file, file.name);
    });

    formData.append('usuarioId', this.userId);

    return formData;
  }

  retornarLinhasExtratoOfx(formData: FormData): void {
    this.spinnerService.show('Enviando arquivo...');

    this.extratoService.getLinhasExtratoOfx(formData).subscribe({
      next: (response) => {
        const responseTemLinhas = response && response.length > 0 &&
                                  response.some(e => e.linhasExtratos && e.linhasExtratos.length > 0);

        this.extratos = this.filtrarLinhasPorMesAnoSelecionado(response);
        this.extratoService.setExtratos(this.extratos);
        this.spinnerService.hide();
        const temLinhasAposFiltro = this.extratos && this.extratos.length > 0;

        if (!responseTemLinhas || !temLinhasAposFiltro) {
          this.displayDialogSemMovimentacao = true;
          this.activeIndex = 1;
        } else {
          this.activeIndex = 2;
        }
        this.cdr.detectChanges();
      },
      error: (error) => {
        const mensagemErro = error?.error?.message || 'Erro ao importar extratos';
        this.toastService.setMensagemToast(mensagemErro, SeveridadeToast.Error);
        this.spinnerService.hide();
        this.activeIndex = 1;
      }
    });
  }

  retornarLinhasExtratoPlanilha(formData: FormData): void {
    this.spinnerService.show('Enviando arquivo...');

    this.extratoService.getLinhasExtratoPlanilha(formData).subscribe({
      next: (response) => {
        const responseTemLinhas = response && response.length > 0 &&
                                  response.some(e => e.linhasExtratos && e.linhasExtratos.length > 0);

        this.extratos = this.filtrarLinhasPorMesAnoSelecionado(response);
        this.extratoService.setExtratos(this.extratos);
        this.spinnerService.hide();
        const temLinhasAposFiltro = this.extratos && this.extratos.length > 0;
        if (!responseTemLinhas || !temLinhasAposFiltro) {
          this.displayDialogSemMovimentacao = true;
          this.activeIndex = 1;
        } else {
          this.activeIndex = 2;
        }
        this.cdr.detectChanges();
      },
      error: (error) => {
        let mensagemErro = 'Erro ao importar extratos';

        if (error.status === 400) {
          if (error.error?.message) {
            mensagemErro = error.error.message;
          } else if (typeof error.error === 'string') {
            mensagemErro = error.error;
          }
        } else if (error.status === 500) {
          if (error.error?.message) {
            mensagemErro = error.error.message;
          } else {
            mensagemErro = 'Erro interno do servidor ao processar a planilha';
          }
        } else if (error.error?.message) {
          mensagemErro = error.error.message;
        }

        this.toastService.setMensagemToast(mensagemErro, SeveridadeToast.Error);
        this.spinnerService.hide();
        this.activeIndex = 1;
      }
    });
  }

  private filtrarLinhasPorMesAnoSelecionado(extratos: Extrato[]): Extrato[] {
    const dataSelecionada = this.importForm.get('data')?.value;
    if (!dataSelecionada) return extratos;
    const data = dataSelecionada instanceof Date ? dataSelecionada : new Date(dataSelecionada);
    const mesSel = data.getMonth() + 1;
    const anoSel = data.getFullYear();

    const filtrados = (extratos || []).map(e => {
      const linhas = (e.linhasExtratos || []).filter(l => {
        if (!l?.dataOperacao) return false;
        const parts = String(l.dataOperacao).split('-');
        if (parts.length < 3) return false;
        const m = parseInt(parts[1], 10);
        const a = parseInt(parts[2], 10);
        return m === mesSel && a === anoSel;
      }).map(l => ({
        ...l,
        permiteAlterarContaContrapartida: l.permiteAlterarContaContrapartida ?? false
      }));
      if (linhas.length === 0) return { ...e, linhasExtratos: [] } as Extrato;
      const primeiroDia = new Date(anoSel, mesSel - 1, 1);
      const ultimoDia = new Date(anoSel, mesSel, 0);
      return {
        ...e,
        mes: mesSel.toString().padStart(2, '0'),
        ano: anoSel.toString(),
        dataInicial: primeiroDia as any,
        dataFinal: ultimoDia as any,
        linhasExtratos: linhas
      } as Extrato;
    }).filter(e => e.linhasExtratos && e.linhasExtratos.length > 0);

    return filtrados;
  }

  onCheckboxChange(linhaExtrato: LinhaExtrato): void {
    // Garante que o campo está definido
    if (linhaExtrato.permiteAlterarContaContrapartida === undefined) {
      linhaExtrato.permiteAlterarContaContrapartida = false;
    }
  }

  confirmaAlteracao(event: Event, linhaExtrato: LinhaExtrato): void {
    const input = event.target as HTMLInputElement;
    const inputValue = input.value;

    this.confirmationService.confirm({
      message: `Tem certeza que deseja alterar a descrição de <b>${linhaExtrato.descricao}</b> para <b>${inputValue}</b>?`,
      header: 'Confirmar alteração da descrição',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        linhaExtrato.descricao = inputValue;
      },
      reject: () => {
        linhaExtrato.descricao = this.valorOriginal;
        input.value = this.valorOriginal;
      }
    });
  }

  valorOriginal: string = '';

  salvarValorOriginal(linhaExtrato: LinhaExtrato): void {
    this.valorOriginal = linhaExtrato.descricao;
  }

  verDetalhesLinhaExtrato(linhaExtrato: LinhaExtrato) {
    const extrato = this.extratos.find((e) =>
      e.linhasExtratos.some((l) => l.codigoTransacao === linhaExtrato.codigoTransacao)
    );

    if (extrato) {
      this.extratoService.setCurrentExtrato(extrato);
    }
    this.router.navigate(['/detalhes-extrato', linhaExtrato.codigoTransacao]);
  }

  getTipoTransacaoLabel(tipoTransacao: number): string {
    return TipoTransacao[tipoTransacao] || 'Desconhecido';
  }

  obterNomeBancoPorCodigo(codigoBanco: string): string {
    if (this.bancoCache[codigoBanco]) {
      return this.bancoCache[codigoBanco];
    }
    return `${codigoBanco} - Carregando...`;
  }

  confirmarImportacao() {
    const temLinhas = this.extratos && this.extratos.length > 0 &&
                      this.extratos.some(e => e.linhasExtratos && e.linhasExtratos.length > 0);
    if (!temLinhas) {
      this.displayDialogSemMovimentacao = true;
      return;
    }

    this.spinnerService.show('Enviando dados do extrato...');

    const dataSelecionada = this.importForm.get('data').getRawValue();
    const dadosValidos = this.validarDatasExtratos(dataSelecionada);

    if (!dadosValidos) {
      this.spinnerService.hide();
      return;
    }

    const extratoSelecionado = this.extratos[0];

    const verificacaoRequest: VerificacaoExistenciaExtratoRequest = {
      mes: extratoSelecionado.mes,
      ano: extratoSelecionado.ano,
      contaBancariaId: extratoSelecionado.contaBancariaId,
      usuarioId: this.userId
    };

    extratoSelecionado.dataInicial = new Date(extratoSelecionado.dataInicial);
    extratoSelecionado.dataFinal = new Date(extratoSelecionado.dataFinal);

    forkJoin({
      contaBancaria: this.contaBancariaService.obterContaBancaria(verificacaoRequest.contaBancariaId),
      extratoExistente: this.extratoService.verificarExtratoExistente(verificacaoRequest)
    }).subscribe({
      next: (result) => {
        const { contaBancaria, extratoExistente } = result;

        if (this.selectedFileExtension === 'ofx') {
          const validacoes = this.validarExtratoOfx(extratoSelecionado, contaBancaria);
          if (!validacoes.valido) {
            this.spinnerService.hide();
            this.toastService.setMensagemToast(validacoes.mensagem, SeveridadeToast.Error);
            return;
          }
        }

        if (extratoExistente) {
          this.spinnerService.hide();
          this.exibirDialogoConfirmacao();
        } else {
          this.importarExtratos();
        }
      },
      error: (error) => {
        this.spinnerService.hide();
        this.toastService.setMensagemToast(
          'Erro ao verificar dados do extrato',
          SeveridadeToast.Error
        );
      }
    });
  }

  private validarExtratoOfx(extratoSelecionado: any, contaBancaria: any): { valido: boolean; mensagem: string } {
    const normalizeBank = (v: any) => {
      const onlyDigits = String(v ?? '').replace(/\D/g, '');
      const trimmed = onlyDigits.replace(/^0+/, '');
      return trimmed || '0';
    };

    const bancoIdOfx = normalizeBank(extratoSelecionado.bancoId);
    const bancoConta = normalizeBank(contaBancaria.banco);

    if (bancoIdOfx !== bancoConta) {
      return {
        valido: false,
        mensagem: 'O banco da conta bancária selecionada precisa ser o mesmo do extrato.'
      };
    }

    const bancosSemVerificacaoAgenciaConta = ['1', '748', '237', '208', '290', '260', '336', '461', '341'];
    if (!bancosSemVerificacaoAgenciaConta.includes(bancoIdOfx) && extratoSelecionado.agencia !== contaBancaria.agencia) {
      return {
        valido: false,
        mensagem: 'A agência da conta bancária selecionada precisa ser a mesma do extrato.'
      };
    }

    if (!bancosSemVerificacaoAgenciaConta.includes(bancoIdOfx) && extratoSelecionado.conta !== contaBancaria.numeroConta) {
      return {
        valido: false,
        mensagem: 'O número da conta bancária selecionada precisa ser o mesmo do extrato.'
      };
    }

    const dataInicialEsperada = new Date(Number(extratoSelecionado.ano), Number(extratoSelecionado.mes) - 1, 1);
    const dataInicial = extratoSelecionado.dataInicial;
    const dataInicialEsperadaISO = dataInicialEsperada.toISOString().split('T')[0];
    const dataInicialISO = new Date(dataInicial).toISOString().split('T')[0];

    if (dataInicialISO !== dataInicialEsperadaISO) {
      return {
        valido: false,
        mensagem: `A data inicial do extrato deve ser o dia 01 de ${dataInicialEsperada.toLocaleDateString('pt-BR', {
          month: 'long',
          year: 'numeric'
        })}.`
      };
    }

    const dataFinal = extratoSelecionado.dataFinal;
    const dataFinalExtrato = new Date(dataFinal);
    const mesExtrato = dataFinalExtrato.getMonth() + 1;
    const anoExtrato = dataFinalExtrato.getFullYear();

    if (mesExtrato !== Number(extratoSelecionado.mes) || anoExtrato !== Number(extratoSelecionado.ano)) {
      return {
        valido: false,
        mensagem: `A data final do extrato deve estar dentro do mês ${extratoSelecionado.mes}/${extratoSelecionado.ano}.`
      };
    }

    return { valido: true, mensagem: '' };
  }

  importarExtratos() {
    this.spinnerService.show('Importando extrato...');

    const usuarioId = this.jwtHelper.getUserIdFromToken();

    this.extratos = this.extratos.map(extrato => ({
      ...extrato,
      usuarioId: usuarioId
    }));

    this.extratoService.importarExtratosDTO(this.extratos).subscribe({
      next: () => {
        this.toastService.setMensagemToast(
          'Extrato importado com sucesso!',
          SeveridadeToast.Success
        );
        this.extratoService.resetState();
        this.skipCanDeactivate = true;
        this.router.navigate(['/lista-extrato']);
        this.spinnerService.hide();

        this.importForm.reset();
        this.activeIndex = 0;
        if (this.fileUploadOfx || this.fileUploadXlsx) {
          this.fileUploadOfx.clear();
          this.fileUploadXlsx.clear();
        }
        localStorage.removeItem('importForm');
      },
      error: (error) => {
        console.error('Erro ao importar extratos:', error);
        this.toastService.setMensagemToast(
          'Erro ao importar extratos',
          SeveridadeToast.Error
        );
        this.spinnerService.hide();
      }
    });
  }

  exibirDialogoConfirmacao() {
    this.displayDialog = true;
  }

  continuarImportacao() {
    this.closeDialog();
    this.importarExtratos();
  }

  cancelarImportacao() {
    this.closeDialog();
  }

  validarDatasExtratos(dataSelecionada: any): boolean {
    if (!dataSelecionada) {
      this.toastService.setMensagemToast(
        'Selecione o período no passo 1 para continuar.',
        SeveridadeToast.Error
      );
      return false;
    }
    if (!(dataSelecionada instanceof Date)) {
      dataSelecionada = new Date(dataSelecionada);
    }

    const mesSelecionado = (dataSelecionada.getMonth() + 1).toString().padStart(2, '0');
    const anoSelecionado = dataSelecionada.getFullYear().toString();
    const hoje = new Date();

    let dadosValidos = true;

    const temLinhas = this.extratos && this.extratos.length > 0 &&
                      this.extratos.some(e => e.linhasExtratos && e.linhasExtratos.length > 0);
    if (!temLinhas) {
      this.toastService.setMensagemToast(
        'Nenhuma linha encontrada para o período selecionado.',
        SeveridadeToast.Error
      );
      return false;
    }

    const dataPeriodo = new Date(parseInt(anoSelecionado, 10), parseInt(mesSelecionado, 10) - 1, 1);
    const primeiroDiaMesSeguinte = new Date(dataPeriodo.getFullYear(), dataPeriodo.getMonth() + 1, 1);
    if (hoje < primeiroDiaMesSeguinte) {
      this.toastService.setMensagemToast(
        'A importação do extrato só é permitida a partir do primeiro dia do mês seguinte ao extrato',
        SeveridadeToast.Error
      );
      dadosValidos = false;
    }

    return dadosValidos;
  }

  loadContasContabeis() {
    this.planoDeContasService.getContasAnaliticasPorUsuario(this.userId).subscribe({
      next: (contas) => {
        this.contasContabeis = contas.map(conta => ({
          nome: conta.descricao,
          id: conta.id
        }));
      },
      error: () => {
        this.toastService.setMensagemToast(
          'Erro ao carregar contas contábeis',
          SeveridadeToast.Error
        );
      },
    });
  }

  private carregarDadosIniciais() {
    this.spinnerService.show('Carregando dados...');

    forkJoin({
      contasBancarias: this.contaBancariaService.listaContasBancarias(this.userId),
      contasContabeis: this.planoDeContasService.getContasAnaliticasPorUsuario(this.userId)
    }).subscribe({
      next: (result) => {
        this.processarContasBancarias(result.contasBancarias);
        this.contasContabeis = result.contasContabeis.map(conta => ({
          nome: conta.descricao,
          id: conta.id
        }));

        this.spinnerService.hide();
      },
      error: () => {
        this.toastService.setMensagemToast(
          'Erro ao carregar dados iniciais',
          SeveridadeToast.Error
        );
        this.spinnerService.hide();
      }
    });
  }

  private processarContasBancarias(contas: ContaBancaria[]) {
    const bancosUnicos = [...new Set(contas.map(conta => conta.banco))];
    const bancoObservables = bancosUnicos.map((codigoBanco) =>
      this.contaBancariaService.obterBancoPorCodigo(codigoBanco).pipe(
        map((banco) => ({ codigo: codigoBanco, nome: banco.name })),
        catchError(() => of({ codigo: codigoBanco, nome: 'Banco desconhecido' }))
      )
    );

    forkJoin(bancoObservables).subscribe({
      next: (bancosInfo) => {
        const bancoCache = bancosInfo.reduce((acc, banco) => {
          acc[banco.codigo] = banco.nome;
          return acc;
        }, {} as { [key: string]: string });

        this.bancoCache = { ...this.bancoCache, ...bancoCache };
        this.contasBancarias = contas.map((conta) => ({
          id: conta.id,
          banco: conta.banco,
          numeroConta: conta.numeroConta,
          label: `${conta.banco} - ${bancoCache[conta.banco] || 'Banco desconhecido'} - ${conta.agencia} - ${conta.numeroConta}`,
          value: conta.id,
        }));
      },
      error: () => {
        this.toastService.setMensagemToast(
          'Erro ao carregar informações dos bancos',
          SeveridadeToast.Error
        );
      }
    });
  }

  private limparCache() {
    this.bancoCache = {};
  }

  formatarDataOperacao(dataOperacao: string): string {
    const [dia, mes, ano] = dataOperacao.split('-').map(part => parseInt(part, 10));
    const data = new Date(ano, mes - 1, dia);
    if (isNaN(data.getTime())) {
      return 'Data inválida';
    }
    return this.formataDataHelper.formatarData(data);
  }

  showDialog() {
    this.displayDialog = true;
  }

  closeDialog() {
    this.displayDialog = false;
  }

  proceed() {
    this.closeDialog();
  }

  onInfoDialogClose() {
    this.displayDialogSemMovimentacao = false;
  }

  temLinhasExtrato(): boolean {
    return this.extratos && this.extratos.length > 0 &&
           this.extratos.some(e => e.linhasExtratos && Array.isArray(e.linhasExtratos) && e.linhasExtratos.length > 0);
  }
}
